import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Activity } from 'lucide-react';

/**
 * Tiny header indicator showing integration health.
 * - Green dot: 100% healthy
 * - Amber dot: some services not configured (healthy_pct < 100 but no failures)
 * - Pulsing red dot: at least one LIVE service has a failed probe in last 24h
 * Click → /app/admin/integrations
 *
 * Polls every 2 minutes.
 */
export default function IntegrationHealthIndicator() {
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get('/v1/integrations/health');
        if (!cancelled) setHealth(res.data);
      } catch {/* silent */}
    };
    load();
    const id = setInterval(load, 120000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!health) return null;

  const probeFailing = (health.failing || []).filter(
    f => f.live && (f.reasons || []).some(r => r === 'probe_failed' || r === 'probe_stale_24h')
  ).length > 0;

  let color = 'bg-emerald-500';
  let pulse = false;
  let title = `All integrations healthy (${health.score}/${health.total})`;
  if (probeFailing) {
    color = 'bg-rose-500'; pulse = true;
    title = `${health.failing.length} integration(s) failing — click to investigate`;
  } else if (health.healthy_pct < 100) {
    color = 'bg-amber-400';
    title = `${health.score}/${health.total} integrations healthy (${health.healthy_pct}%) — click to configure`;
  }

  return (
    <button
      type="button"
      onClick={() => navigate('/app/admin/integrations')}
      className="relative h-8 w-8 inline-flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-md transition-colors"
      title={title}
      data-testid="integration-health-indicator"
    >
      <Activity className="w-4 h-4" />
      <span className="absolute top-1 right-1 flex h-2 w-2">
        {pulse && (
          <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping ${color}`} />
        )}
        <span className={`relative inline-flex rounded-full h-2 w-2 ${color}`} />
      </span>
    </button>
  );
}
